import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

function formatElapsed(ms) {
  if (ms == null) return "--:--";
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60).toString().padStart(2, "0");
  const s = (totalSec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function AdminPanel() {
  const [gameName, setGameName] = useState("");
  const [games, setGames] = useState([]);
  const [selectedGameId, setSelectedGameId] = useState("");

  // Checkpoints
  const [checkpoints, setCheckpoints] = useState([]);
  const [cpName, setCpName] = useState("");
  const [cpLat, setCpLat] = useState("");
  const [cpLng, setCpLng] = useState("");
  const [cpRadius, setCpRadius] = useState("50");
  const [cpType, setCpType] = useState("checkpoint");

  // Templates
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");

  // Teams
  const [teams, setTeams] = useState([]);
  const [unassignedUsers, setUnassignedUsers] = useState([]);
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamUserId, setNewTeamUserId] = useState("");

  // ─── Fetch helpers ───────────────────────────────────────────────────────────

  const fetchGames = useCallback(async () => {
    try {
      const res = await axios.get("/api/games", { withCredentials: true });
      setGames(res.data);
    } catch (err) {
      console.error("Failed to fetch games:", err);
    }
  }, []);

  const fetchCheckpoints = useCallback(async (gameId) => {
    try {
      const res = await axios.get(`/api/games/${gameId}/checkpoints`, { withCredentials: true });
      setCheckpoints(res.data);
    } catch (err) {
      console.error("Failed to fetch checkpoints:", err);
    }
  }, []);

  const fetchTeams = useCallback(async (gameId) => {
    try {
      const res = await axios.get(`/api/games/${gameId}/teams`, { withCredentials: true });
      setTeams(res.data);
    } catch (err) {
      console.error("Failed to fetch teams:", err);
    }
  }, []);

  const fetchUnassignedUsers = useCallback(async (gameId) => {
    try {
      const res = await axios.get(`/api/games/${gameId}/unassigned-users`, { withCredentials: true });
      setUnassignedUsers(res.data);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get('/api/checkpoint-templates', { withCredentials: true });
      setTemplates(res.data);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    }
  }, []);

  // ─── Polling ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchGames();
    fetchTemplates();
    const t = setInterval(fetchGames, 5000);
    return () => clearInterval(t);
  }, [fetchGames, fetchTemplates]);

  useEffect(() => {
    if (!selectedGameId) {
      setCheckpoints([]);
      setTeams([]);
      setUnassignedUsers([]);
      return;
    }

    fetchCheckpoints(selectedGameId);
    fetchTeams(selectedGameId);
    fetchUnassignedUsers(selectedGameId);

    // Poll team progress every 3s (for live elapsed times)
    const t = setInterval(() => {
      fetchTeams(selectedGameId);
    }, 3000);

    return () => clearInterval(t);
  }, [selectedGameId, fetchCheckpoints, fetchTeams, fetchUnassignedUsers]);

  // ─── Game actions ─────────────────────────────────────────────────────────────

  const createGame = async () => {
    if (!gameName.trim()) return alert("Enter a game name.");
    try {
      await axios.post("/api/games", { name: gameName }, { withCredentials: true });
      setGameName("");
      fetchGames();
    } catch {
      alert("Error creating game");
    }
  };

  const startGame = async () => {
    if (!selectedGameId) return;
    try {
      await axios.post(`/api/games/${selectedGameId}/start`, {}, { withCredentials: true });
      alert("Game started! Now use the Start buttons to send teams off.");
      fetchGames();
    } catch (err) {
      alert(err.response?.data?.error || "Error starting game");
    }
  };

  const endGame = async () => {
    if (!selectedGameId) return;
    if (!window.confirm("End the game and clear all teams/checkpoints?")) return;
    try {
      await axios.post(`/api/games/${selectedGameId}/end`, {}, { withCredentials: true });
      alert("Game ended.");
      setSelectedGameId("");
      fetchGames();
    } catch (err) {
      alert(err.response?.data?.error || "Error ending game");
    }
  };

  // ─── Checkpoint actions ───────────────────────────────────────────────────────

  const addCheckpoint = async () => {
    if (!cpName.trim() || !cpLat || !cpLng) return alert("Name, lat, and lng are required.");
    try {
      await axios.post(
        `/api/games/${selectedGameId}/checkpoints`,
        { name: cpName, lat: cpLat, lng: cpLng, radius: cpRadius || 50, type: cpType },
        { withCredentials: true }
      );
      setCpName(""); setCpLat(""); setCpLng(""); setCpRadius("50"); setCpType("checkpoint");
      fetchCheckpoints(selectedGameId);
    } catch (err) {
      alert(err.response?.data?.error || "Error adding checkpoint");
    }
  };

  const removeCheckpoint = async (cpId) => {
    try {
      await axios.delete(`/api/games/${selectedGameId}/checkpoints/${cpId}`, { withCredentials: true });
      fetchCheckpoints(selectedGameId);
    } catch {
      alert("Error removing checkpoint");
    }
  };

  // ─── Template actions ─────────────────────────────────────────────────────────

  const saveAsTemplate = async () => {
    if (!templateName.trim()) return alert("Enter a name for this template.");
    try {
      await axios.post(
        `/api/games/${selectedGameId}/save-as-template`,
        { name: templateName.trim() },
        { withCredentials: true }
      );
      setTemplateName("");
      fetchTemplates();
      alert(`Template "${templateName.trim()}" saved!`);
    } catch (err) {
      alert(err.response?.data?.error || "Error saving template");
    }
  };

  const loadTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Load "${templateName}"? This will replace all current checkpoints for this game.`)) return;
    try {
      await axios.post(
        `/api/games/${selectedGameId}/load-template/${templateId}`,
        {},
        { withCredentials: true }
      );
      fetchCheckpoints(selectedGameId);
    } catch (err) {
      alert(err.response?.data?.error || "Error loading template");
    }
  };

  const deleteTemplate = async (templateId, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await axios.delete(`/api/checkpoint-templates/${templateId}`, { withCredentials: true });
      fetchTemplates();
    } catch {
      alert("Error deleting template");
    }
  };

  // ─── Team actions ─────────────────────────────────────────────────────────────

  const createTeam = async () => {
    if (!newTeamName.trim() || !newTeamUserId) return alert("Enter team name and select a user.");
    try {
      await axios.post(
        `/api/games/${selectedGameId}/teams`,
        { teamName: newTeamName, userId: newTeamUserId },
        { withCredentials: true }
      );
      setNewTeamName(""); setNewTeamUserId("");
      fetchTeams(selectedGameId);
      fetchUnassignedUsers(selectedGameId);
    } catch (err) {
      alert(err.response?.data?.error || "Error creating team");
    }
  };

  const removeTeam = async (teamId) => {
    try {
      await axios.delete(`/api/games/${selectedGameId}/teams/${teamId}`, { withCredentials: true });
      fetchTeams(selectedGameId);
      fetchUnassignedUsers(selectedGameId);
    } catch {
      alert("Error removing team");
    }
  };

  // ─── UI helpers ───────────────────────────────────────────────────────────────

  const selectedGame = games.find(g => g._id === selectedGameId);

  return (
    <div style={{ padding: "20px", maxWidth: "700px" }}>
      <h2 style={{ fontSize: "20px", fontWeight: "bold" }}>Admin Panel</h2>

      {/* ── Create Game ── */}
      <div style={{ marginTop: "16px" }}>
        <label style={{ fontWeight: "bold" }}>Create New Game</label>
        <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
          <input
            type="text"
            placeholder="Game name"
            value={gameName}
            onChange={e => setGameName(e.target.value)}
            style={{ border: "1px solid #ccc", padding: "6px 10px", borderRadius: "4px", flex: 1 }}
          />
          <button
            onClick={createGame}
            style={{ background: "#2563eb", color: "white", padding: "6px 14px", borderRadius: "4px", border: "none", cursor: "pointer" }}
          >
            Create
          </button>
        </div>
      </div>

      {/* ── Game List ── */}
      <div style={{ marginTop: "24px" }}>
        <h3 style={{ fontWeight: "600", marginBottom: "8px" }}>Game Sessions</h3>
        {games.length === 0 ? (
          <p style={{ color: "#6b7280" }}>No games yet.</p>
        ) : (
          games.map(game => (
            <div
              key={game._id}
              style={{
                border: selectedGameId === game._id ? "3px solid #2563eb" : "2px solid #e5e7eb",
                background: selectedGameId === game._id ? "#eff6ff" : "#fff",
                borderRadius: "8px", padding: "10px 14px", marginBottom: "8px",
                display: "flex", justifyContent: "space-between", alignItems: "center"
              }}
            >
              <div>
                <div style={{ fontWeight: "bold" }}>{game.name}</div>
                <div style={{ fontSize: "13px", color: "#6b7280" }}>
                  {game.started ? "🟢 Live" : "🕒 Waiting"}
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedGameId(game._id);
                  window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
                }}
                style={{ background: "#2563eb", color: "white", padding: "6px 12px", borderRadius: "4px", border: "none", cursor: "pointer" }}
              >
                Manage
              </button>
            </div>
          ))
        )}
      </div>

      {/* ── Selected Game Management ── */}
      {selectedGameId && (
        <>
          <h3 style={{ fontWeight: "bold", marginTop: "24px", fontSize: "16px" }}>
            Managing: {selectedGame?.name}
          </h3>

          {/* Game controls */}
          <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
            <button
              onClick={startGame}
              disabled={!!selectedGame?.started}
              style={{
                background: selectedGame?.started ? "#6b7280" : "#16a34a",
                color: "white", padding: "8px 16px", borderRadius: "6px",
                border: "none", cursor: selectedGame?.started ? "default" : "pointer"
              }}
            >
              {selectedGame?.started ? "Game Live" : "Start Game"}
            </button>
            <button
              onClick={endGame}
              style={{ background: "#dc2626", color: "white", padding: "8px 16px", borderRadius: "6px", border: "none", cursor: "pointer" }}
            >
              End Game
            </button>
          </div>

          {/* ── Checkpoints ── */}
          <div style={{ marginTop: "24px" }}>
            <h4 style={{ fontWeight: "bold", marginBottom: "10px" }}>
              Checkpoints ({checkpoints.length})
            </h4>

            {/* Add checkpoint form */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "10px" }}>
              <input
                type="text"
                placeholder="Name (e.g. Capitol)"
                value={cpName}
                onChange={e => setCpName(e.target.value)}
                style={{ border: "1px solid #ccc", padding: "6px 10px", borderRadius: "4px", gridColumn: "1/-1" }}
              />
              <input
                type="number"
                placeholder="Latitude"
                value={cpLat}
                onChange={e => setCpLat(e.target.value)}
                step="0.000001"
                style={{ border: "1px solid #ccc", padding: "6px 10px", borderRadius: "4px" }}
              />
              <input
                type="number"
                placeholder="Longitude"
                value={cpLng}
                onChange={e => setCpLng(e.target.value)}
                step="0.000001"
                style={{ border: "1px solid #ccc", padding: "6px 10px", borderRadius: "4px" }}
              />
              <input
                type="number"
                placeholder="Radius (m, default 50)"
                value={cpRadius}
                onChange={e => setCpRadius(e.target.value)}
                style={{ border: "1px solid #ccc", padding: "6px 10px", borderRadius: "4px" }}
              />
              <select
                value={cpType}
                onChange={e => setCpType(e.target.value)}
                style={{ border: "1px solid #ccc", padding: "6px 10px", borderRadius: "4px" }}
              >
                <option value="start">Start</option>
                <option value="finish">Finish</option>
                <option value="checkpoint">Checkpoint</option>
              </select>
              <button
                onClick={addCheckpoint}
                style={{ background: "#10b981", color: "white", padding: "8px", borderRadius: "6px", border: "none", cursor: "pointer" }}
              >
                Add
              </button>
            </div>

            {checkpoints.length === 0 ? (
              <p style={{ color: "#6b7280", fontSize: "14px" }}>No checkpoints yet.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                <thead>
                  <tr style={{ background: "#f3f4f6" }}>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Type</th>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Name</th>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Lat</th>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Lng</th>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Radius</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {checkpoints.map(cp => {
                    const badge = cp.type === 'start'
                      ? { bg: "#d1fae5", color: "#065f46", label: "START" }
                      : cp.type === 'finish'
                        ? { bg: "#fef3c7", color: "#92400e", label: "FINISH" }
                        : { bg: "#e0e7ff", color: "#3730a3", label: "CP" };
                    return (
                      <tr key={cp._id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                        <td style={{ padding: "6px 8px" }}>
                          <span style={{ background: badge.bg, color: badge.color, padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: "bold" }}>
                            {badge.label}
                          </span>
                        </td>
                        <td style={{ padding: "6px 8px" }}>{cp.name}</td>
                        <td style={{ padding: "6px 8px" }}>{cp.lat.toFixed(5)}</td>
                        <td style={{ padding: "6px 8px" }}>{cp.lng.toFixed(5)}</td>
                        <td style={{ padding: "6px 8px" }}>{cp.radius}m</td>
                        <td style={{ padding: "6px 8px" }}>
                          <button
                            onClick={() => removeCheckpoint(cp._id)}
                            style={{ background: "#dc2626", color: "white", border: "none", borderRadius: "4px", padding: "4px 8px", cursor: "pointer", fontSize: "12px" }}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* Save / Load templates */}
            <div style={{ marginTop: "16px", padding: "12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
              <div style={{ fontWeight: "bold", fontSize: "13px", marginBottom: "10px", color: "#374151" }}>
                Checkpoint Templates
              </div>

              {/* Save current as template */}
              {checkpoints.length > 0 && (
                <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                  <input
                    type="text"
                    placeholder="Template name (e.g. Downtown Loop)"
                    value={templateName}
                    onChange={e => setTemplateName(e.target.value)}
                    style={{ border: "1px solid #ccc", padding: "6px 10px", borderRadius: "4px", flex: 1, fontSize: "13px" }}
                  />
                  <button
                    onClick={saveAsTemplate}
                    style={{ background: "#0ea5e9", color: "white", border: "none", borderRadius: "4px", padding: "6px 12px", cursor: "pointer", fontSize: "13px", whiteSpace: "nowrap" }}
                  >
                    Save as Template
                  </button>
                </div>
              )}

              {/* Load from template */}
              {templates.length === 0 ? (
                <p style={{ color: "#9ca3af", fontSize: "13px", margin: 0 }}>No saved templates yet.</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {templates.map(t => (
                    <div key={t._id} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      background: "#fff", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "6px 10px"
                    }}>
                      <div>
                        <span style={{ fontWeight: "bold", fontSize: "13px" }}>{t.name}</span>
                        <span style={{ color: "#6b7280", fontSize: "12px", marginLeft: "8px" }}>
                          {t.checkpoints.length} checkpoint{t.checkpoints.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: "6px" }}>
                        <button
                          onClick={() => loadTemplate(t._id, t.name)}
                          style={{ background: "#7c3aed", color: "white", border: "none", borderRadius: "4px", padding: "4px 10px", cursor: "pointer", fontSize: "12px" }}
                        >
                          Load
                        </button>
                        <button
                          onClick={() => deleteTemplate(t._id, t.name)}
                          style={{ background: "#dc2626", color: "white", border: "none", borderRadius: "4px", padding: "4px 8px", cursor: "pointer", fontSize: "12px" }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Teams ── */}
          <div style={{ marginTop: "28px" }}>
            <h4 style={{ fontWeight: "bold", marginBottom: "10px" }}>
              Teams ({teams.length})
            </h4>

            {/* Create team */}
            <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
              <input
                type="text"
                placeholder="Team name"
                value={newTeamName}
                onChange={e => setNewTeamName(e.target.value)}
                style={{ border: "1px solid #ccc", padding: "6px 10px", borderRadius: "4px", flex: "1 1 140px" }}
              />
              <select
                value={newTeamUserId}
                onChange={e => setNewTeamUserId(e.target.value)}
                style={{ border: "1px solid #ccc", padding: "6px 10px", borderRadius: "4px", flex: "1 1 140px" }}
              >
                <option value="">Select player…</option>
                {unassignedUsers.map(u => (
                  <option key={u._id} value={u._id}>{u.username}</option>
                ))}
              </select>
              <button
                onClick={createTeam}
                style={{ background: "#7c3aed", color: "white", padding: "8px 14px", borderRadius: "6px", border: "none", cursor: "pointer" }}
              >
                Add Team
              </button>
            </div>

            {/* Team progress table */}
            {teams.length === 0 ? (
              <p style={{ color: "#6b7280", fontSize: "14px" }}>No teams yet.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                <thead>
                  <tr style={{ background: "#f3f4f6" }}>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Team</th>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Player</th>
                    <th style={{ padding: "6px 8px", textAlign: "center" }}>Progress</th>
                    <th style={{ padding: "6px 8px", textAlign: "right" }}>Time</th>
                    <th style={{ padding: "6px 8px", textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teams.map(t => (
                    <tr key={t._id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                      <td style={{ padding: "6px 8px", fontWeight: "bold" }}>{t.teamName}</td>
                      <td style={{ padding: "6px 8px", color: "#6b7280" }}>{t.username}</td>
                      <td style={{ padding: "6px 8px", textAlign: "center" }}>
                        <span style={{
                          background: t.finished ? "#d1fae5" : "#fef9c3",
                          padding: "2px 8px", borderRadius: "12px", fontSize: "12px"
                        }}>
                          {t.finished ? "Done" : `${t.checkpointsCollected}/${t.totalCheckpoints}`}
                        </span>
                      </td>
                      <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "monospace" }}>
                        {t.finished
                          ? formatElapsed(t.elapsedMs)
                          : t.raceStarted
                            ? formatElapsed(t.elapsedMs)
                            : "—"}
                      </td>
                      <td style={{ padding: "6px 8px", textAlign: "center" }}>
                        <div style={{ display: "flex", gap: "6px", justifyContent: "center", alignItems: "center" }}>
                          {!t.raceStarted
                            ? <span style={{ color: "#f59e0b", fontSize: "12px" }}>At start</span>
                            : t.finished
                              ? <span style={{ color: "#10b981", fontSize: "12px" }}>Finished</span>
                              : <span style={{ color: "#16a34a", fontSize: "12px" }}>Racing</span>
                          }
                          <button
                            onClick={() => removeTeam(t._id)}
                            style={{ background: "#dc2626", color: "white", border: "none", borderRadius: "4px", padding: "4px 8px", cursor: "pointer", fontSize: "12px" }}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
